import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

const AgentDockContext = createContext(null);

export function AgentDockProvider({ children }) {
  const [open, setOpen] = useState(false);
  /** idle | push | dive | emerge | open */
  const [phase, setPhase] = useState("idle");
  const openSequenceRef = useRef(null);

  const registerOpenSequence = useCallback((fn) => {
    openSequenceRef.current = fn;
  }, []);

  const openDock = useCallback(() => {
    if (openSequenceRef.current) {
      openSequenceRef.current();
      return;
    }
    setOpen(true);
    setPhase("open");
  }, []);

  const closeDock = useCallback(() => {
    setOpen(false);
    setPhase("idle");
  }, []);

  const toggleDock = useCallback(() => {
    setOpen((v) => {
      if (v) setPhase("idle");
      else setPhase("open");
      return !v;
    });
  }, []);

  const beginOpenSequence = useCallback(() => {
    setPhase("peek");
  }, []);

  const advanceToPush = useCallback(() => {
    setPhase("push");
    setOpen(true);
  }, []);

  const advanceToDive = useCallback(() => {
    setPhase("dive");
  }, []);

  const advanceToEmerge = useCallback(() => {
    setPhase("emerge");
  }, []);

  const settleOpen = useCallback(() => {
    setPhase("open");
  }, []);

  const value = useMemo(
    () => ({
      open,
      phase,
      openDock,
      closeDock,
      toggleDock,
      beginOpenSequence,
      advanceToPush,
      advanceToDive,
      advanceToEmerge,
      settleOpen,
      registerOpenSequence,
    }),
    [
      open,
      phase,
      openDock,
      closeDock,
      toggleDock,
      beginOpenSequence,
      advanceToPush,
      advanceToDive,
      advanceToEmerge,
      settleOpen,
      registerOpenSequence,
    ]
  );

  return <AgentDockContext.Provider value={value}>{children}</AgentDockContext.Provider>;
}

export function useAgentDock() {
  const ctx = useContext(AgentDockContext);
  if (!ctx) {
    throw new Error("useAgentDock must be used within AgentDockProvider");
  }
  return ctx;
}

/** Safe hook for Layout when provider is always present. */
export function useAgentDockOptional() {
  return useContext(AgentDockContext);
}
